import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  SupplierAnnouncementDetailDto,
  SupplierAnnouncementListDto,
  SupplierAnnouncementReadAckDto,
} from '@/api/supplier-announcements-client';
import { useSupplierAnnouncementsClient } from '@/api/supplier-announcements-client-context';

// =============================================================================
// use-supplier-announcements.ts — Hooks TanStack Query de la RÉCEPTION FOURNISSEUR d'annonces (R15-COM-07).
// =============================================================================
// Lecture PURE (useQuery) + accusé de lecture (useMutation). Le client effectif vient du
// SupplierAnnouncementsClientProvider (doublable). ZÉRO règle métier : l'autorité (session/withTenant, tri,
// statut lu/non-lu) est l'API. Aucune donnée métier en localStorage (RULE_REF_006).

const ROOT = ['supplier-announcements'] as const;

/** Clé de query de la liste (par filtre campagne éventuel). */
export function supplierAnnouncementsKey(campaignId: string | null): readonly unknown[] {
  return [...ROOT, 'list', campaignId ?? 'all'];
}

/** Clé de query d'un détail (par annonce). */
export function supplierAnnouncementDetailKey(announcementId: string): readonly unknown[] {
  return [...ROOT, 'detail', announcementId];
}

/**
 * GET liste des annonces reçues (onglet §3.9). `enabled` évite l'appel tant que l'onglet n'est pas actif
 * (le tablist Historique charge la liste À l'activation de l'onglet, pas au montage de l'écran).
 */
export function useSupplierAnnouncements(
  campaignId: string | null,
  enabled: boolean,
): UseQueryResult<SupplierAnnouncementListDto, Error> {
  const client = useSupplierAnnouncementsClient();
  return useQuery<SupplierAnnouncementListDto, Error>({
    queryKey: supplierAnnouncementsKey(campaignId),
    queryFn: () => client.list(campaignId ?? undefined),
    enabled,
  });
}

/**
 * GET détail (LECTURE SEULE) d'une annonce reçue. Activé seulement quand une annonce est sélectionnée
 * (ouverture du détail) : `enabled` évite l'appel tant qu'aucune annonce n'est ouverte.
 */
export function useSupplierAnnouncementDetail(
  announcementId: string | null,
): UseQueryResult<SupplierAnnouncementDetailDto, Error> {
  const client = useSupplierAnnouncementsClient();
  return useQuery<SupplierAnnouncementDetailDto, Error>({
    queryKey: supplierAnnouncementDetailKey(announcementId ?? ''),
    queryFn: () => client.getDetail(announcementId as string),
    enabled: announcementId !== null && announcementId.length > 0,
  });
}

/**
 * Accuse la lecture d'une annonce (read_at, DBU §3.9 statut lu/non-lu). À la réussite : invalide la liste
 * + le détail (statut recalculé serveur — autorité). Idempotent (2ᵉ appel = no-op serveur).
 */
export function useMarkAnnouncementRead(): UseMutationResult<
  SupplierAnnouncementReadAckDto,
  Error,
  string
> {
  const client = useSupplierAnnouncementsClient();
  const queryClient = useQueryClient();
  return useMutation<SupplierAnnouncementReadAckDto, Error, string>({
    mutationFn: (announcementId: string) => client.markRead(announcementId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...ROOT] });
    },
  });
}
